﻿/**
 * DSM Components — Behavior Scripts
 *
 * 此文件负责有交互行为的 DSM 组件。
 * 纯样式组件（Button、Badge、Tag）使用 dsm-components.css + dsm-*.liquid 实现。
 *
 * 包含：
 *   DsmDialog — Dialog 对话框控制器
 */

/* ============================================================
   DsmToast — 轻提示控制器
   Figma: https://www.figma.com/design/DaaNEpELVLq1oWo540GsFW/DSM?node-id=4303-1433

   架构（参考 Element Plus message 前向链式堆叠）：
     - 每条 toast 独立 position:fixed，JS 计算 top/bottom 写入 inline style
     - 算法：offset(n) = offset(n-1) + height(n-1) + gap；第一条 = initialOffset
     - ResizeObserver 监听各条高度变化 → 自动触发后续条目重排
     - 关闭一条后从数组删除 → 重新计算后续偏移 → CSS transition 平滑位移

   API：
     DsmToast.show(options)              → toastId (string)
     DsmToast.dismiss(id)                → void
     DsmToast.closeAll(placement?)       → 关闭某 placement 所有，或全部

   options：
     message      {string}   必填
     tone         {string}   default | success | warning | error（默认 default）
     placement    {string}   top-right(默认) | top-left | top-center
                             bottom-right | bottom-left | bottom-center
     offset       {number}   距视口边缘初始距离 px（默认 16）
     gap          {number}   两条 toast 间距 px（默认 8）
     duration     {number}   自动消失 ms；0 = 不自动消失
                             默认：default/success 3000，warning/error 5000，带 action 至少 5000
     showClose    {boolean}  是否显示关闭按钮（默认 true）
     action       {string}   action 按钮文字（可选）
     onAction     {Function} action 点击回调（可选）
     onClose      {Function} 关闭后回调（可选）
     grouping     {boolean}  相同 message+tone 去重合并，计数 badge（默认 false）
     appendTo     {Element|string} 挂载目标，默认 document.body

   自定义事件（toast 元素上冒泡）：
     dsm:toast:show    { id, options }
     dsm:toast:dismiss { id }
   ============================================================ */

/* ============================================================
   DsmDialog - Dialog controller
   Controls .dsm-overlay / .dsm-dialog open/close, focus trap, ESC close

   API - static dialog (Liquid pre-rendered):
     DsmDialog.open('my-dialog')
     DsmDialog.close('my-dialog')

   API - dynamic dialog (JS-created, auto-destroyed on close):
     DsmDialog.open({
       title:          'Remove this item?',
       desc:           'This will be removed.',
       confirmLabel:   'Remove',
       confirmType:    'theme',
       cancelLabel:    'Cancel',
       stack:          false,
       closeOnOverlay: true,
       onConfirm:      function() { ... },
       onCancel:       function() { ... },
     })

   HTML trigger (no JS needed):
     <button data-dsm-dialog-open="my-dialog">Open</button>

   Custom events (on overlay element):
     dsm:dialog:confirm / dsm:dialog:cancel / dsm:dialog:open / dsm:dialog:close
   ============================================================ */

const DsmDialog = (() => {
  let _activeOverlay = null;
  let _previousFocus = null;

  function _getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
        'select:not([disabled]), textarea:not([disabled]), ' +
        '[tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[hidden]') && !el.closest('[aria-hidden="true"]'));
  }

  function _trapFocus(e) {
    if (!_activeOverlay || e.key !== 'Tab') return;
    const focusable = _getFocusable(_activeOverlay);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  function _onKeydown(e) {
    if (e.key === 'Escape' && _activeOverlay) {
      const id = _activeOverlay.dataset.dsmDialog;
      _activeOverlay.dispatchEvent(new CustomEvent('dsm:dialog:cancel', { bubbles: true }));
      close(id);
    }
    _trapFocus(e);
  }

  function _onOverlayClick(e) {
    if (e.target !== e.currentTarget) return;
    if (e.currentTarget.hasAttribute('data-dsm-no-overlay-close')) return;
    var id = e.currentTarget.dataset.dsmDialog;
    e.currentTarget.dispatchEvent(new CustomEvent('dsm:dialog:cancel', { bubbles: true }));
    close(id);
  }

  function _escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function _buildOverlay(opts) {
    var id = 'dsm-dialog-dynamic-' + Date.now();
    var title = opts.title || '';
    var desc = opts.desc || '';
    var confirmLabel = opts.confirmLabel || 'Confirm';
    var confirmType = opts.confirmType || 'primary';
    var cancelLabel = opts.cancelLabel || '';
    var stack = opts.stack ? ' dsm-dialog__footer--stack' : '';

    var cancelHtml = cancelLabel
      ? '<button class="dsm-btn dsm-btn--secondary dsm-btn--medium js-dsm-dialog-cancel" type="button" style="border-radius:999px;min-width:108px;">' +
      '<span class="dsm-btn__label">' + _escHtml(cancelLabel) + '</span>' +
      '</button>'
      : '';

    var confirmHtml =
      '<button class="dsm-btn dsm-btn--' + confirmType + ' dsm-btn--medium js-dsm-dialog-confirm" type="button" style="border-radius:999px;min-width:108px;">' +
      '<span class="dsm-btn__label">' + _escHtml(confirmLabel) + '</span>' +
      '</button>';

    var descHtml = desc
      ? '<div class="dsm-dialog__body"><p class="dsm-dialog__desc" id="' + id + '-desc">' + _escHtml(desc) + '</p></div>'
      : '';

    var overlay = document.createElement('div');
    overlay.className = 'dsm-overlay';
    overlay.id = id + '-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', id + '-title');
    overlay.setAttribute('data-dsm-dialog', id);
    overlay.setAttribute('data-dsm-dynamic', '');
    if (opts.closeOnOverlay === false) overlay.setAttribute('data-dsm-no-overlay-close', '');
    if (desc) overlay.setAttribute('aria-describedby', id + '-desc');

    overlay.innerHTML =
      '<div class="dsm-dialog" role="document">' +
      '<div class="dsm-dialog__header">' +
      '<p class="dsm-dialog__title" id="' + id + '-title">' + _escHtml(title) + '</p>' +
      '</div>' +
      descHtml +
      '<div class="dsm-dialog__footer' + stack + '">' +
      cancelHtml +
      confirmHtml +
      '</div>' +
      '<button class="dsm-dialog__close js-dsm-dialog-cancel" type="button" aria-label="Close dialog">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<line x1="18" y1="6" x2="6" y2="18"/>' +
      '<line x1="6" y1="6" x2="18" y2="18"/>' +
      '</svg>' +
      '</button>' +
      '</div>';

    if (typeof opts.onConfirm === 'function') {
      overlay._dsmOnConfirm = opts.onConfirm;
    }
    if (typeof opts.onCancel === 'function') {
      overlay.addEventListener('dsm:dialog:cancel', opts.onCancel, { once: true });
    }

    overlay.addEventListener('dsm:dialog:close', function () {
      setTimeout(function () {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      }, 250);
    }, { once: true });

    document.body.appendChild(overlay);
    return id;
  }

  function _setLoading(btn, loading) {
    if (loading) {
      btn.classList.add('is-loading');
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.classList.remove('is-loading');
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-busy');
    }
  }

  function open(idOrOpts) {
    var triggerEl = document.activeElement;
    if (typeof idOrOpts === 'object' && idOrOpts !== null) {
      var id = _buildOverlay(idOrOpts);
      requestAnimationFrame(function () { _activate(id, triggerEl); });
    } else {
      _activate(idOrOpts, triggerEl);
    }
  }

  function _activate(id, triggerEl) {
    var overlay = document.querySelector('[data-dsm-dialog="' + id + '"]');
    if (!overlay) {
      console.warn('[DsmDialog] No dialog found with id: "' + id + '"');
      return;
    }
    _previousFocus = (triggerEl && triggerEl !== document.body) ? triggerEl : document.activeElement;
    document.documentElement.classList.add('dsm-scroll-lock');
    overlay.classList.add('is-open');
    _activeOverlay = overlay;

    var focusable = _getFocusable(overlay);
    if (focusable.length) {
      var closeBtn = overlay.querySelector('.dsm-dialog__close');
      (closeBtn || focusable[0]).focus();
    }

    document.addEventListener('keydown', _onKeydown);
    overlay.addEventListener('click', _onOverlayClick);
    overlay.dispatchEvent(new CustomEvent('dsm:dialog:open', { bubbles: true }));
  }

  function close(id) {
    var overlay = document.querySelector('[data-dsm-dialog="' + id + '"]');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    _activeOverlay = null;
    document.documentElement.classList.remove('dsm-scroll-lock');
    document.removeEventListener('keydown', _onKeydown);
    overlay.removeEventListener('click', _onOverlayClick);
    if (_previousFocus && typeof _previousFocus.focus === 'function' && _previousFocus.isConnected !== false) {
      _previousFocus.focus();
    }
    _previousFocus = null;
    overlay.dispatchEvent(new CustomEvent('dsm:dialog:close', { bubbles: true }));
  }

  function init() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-dsm-dialog-open]');
      if (trigger) { e.preventDefault(); open(trigger.dataset.dsmDialogOpen); }
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.js-dsm-dialog-confirm');
      if (!btn) return;
      var overlay = btn.closest('[data-dsm-dialog]');
      if (!overlay) return;
      var id = overlay.dataset.dsmDialog;
      var asyncConfirm = overlay._dsmOnConfirm;

      if (typeof asyncConfirm === 'function') {
        var result = asyncConfirm();
        if (result && typeof result.then === 'function') {
          _setLoading(btn, true);
          result.then(
            function () {
              _setLoading(btn, false);
              overlay.dispatchEvent(new CustomEvent('dsm:dialog:confirm', { bubbles: true }));
              close(id);
            },
            function () {
              _setLoading(btn, false);
            }
          );
          return;
        }
      }
      overlay.dispatchEvent(new CustomEvent('dsm:dialog:confirm', { bubbles: true }));
      close(id);
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.js-dsm-dialog-cancel');
      if (!btn) return;
      var overlay = btn.closest('[data-dsm-dialog]');
      if (!overlay) return;
      overlay.dispatchEvent(new CustomEvent('dsm:dialog:cancel', { bubbles: true }));
      close(overlay.dataset.dsmDialog);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { open: open, close: close };
})();

window.DsmDialog = DsmDialog;

/* ============================================================ */
const DsmToast = (() => {
  var DEFAULT_DURATION = { 'default': 3000, 'success': 3000, 'warning': 5000, 'error': 5000 };
  var DEFAULT_OFFSET = 16;
  var DEFAULT_GAP = 8;
  var EXIT_MS = 220;

  var VALID_PLACEMENTS = [
    'top-right', 'top-left', 'top-center',
    'bottom-right', 'bottom-left', 'bottom-center'
  ];
  var DEFAULT_PLACEMENT = 'top-right';

  /*
   * _instances: placement → Array<entry>
   * entry = { id, el, height, offset, opts, timerId, remaining, startedAt, ro }
   */
  var _instances = {};
  var _idCounter = 0;

  var ICONS = {
    success: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M5.5 10.5L8.5 13.5L14.5 7.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    warning: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M10 6V11" stroke="white" stroke-width="1.5" stroke-linecap="round"/><circle cx="10" cy="13.5" r="0.75" fill="white"/></svg>',
    error: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M7 7L13 13M13 7L7 13" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg>'
  };
  var CLOSE_ICON = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  /* ── 取 placement 的实例列表 ── */
  function _getList(placement) {
    if (!_instances[placement]) _instances[placement] = [];
    return _instances[placement];
  }

  /* ── 解析 appendTo ── */
  function _resolveContainer(appendTo) {
    if (!appendTo) return document.body;
    if (typeof appendTo === 'string') {
      var el = document.querySelector(appendTo);
      return el || document.body;
    }
    return appendTo;
  }

  /* ── 水平方向 inline style ── */
  function _hStyle(placement, offset) {
    if (placement.endsWith('right')) return 'right:' + offset + 'px;';
    if (placement.endsWith('left')) return 'left:' + offset + 'px;';
    /* center */
    return 'left:50%;transform:translateX(-50%);';
  }

  /* ── 垂直方向属性名 ── */
  function _vProp(placement) {
    return placement.startsWith('bottom') ? 'bottom' : 'top';
  }

  /* ── 计算指定 placement 中第 idx 条的 top/bottom 值 ── */
  function _calcOffset(list, idx, initialOffset, gap) {
    if (idx === 0) return initialOffset;
    var prev = list[idx - 1];
    return prev.offset + prev.height + gap;
  }

  /* ── 重新计算某 placement 所有条目的位置并写入 inline style ── */
  function _reflow(placement) {
    var list = _getList(placement);
    var vProp = _vProp(placement);
    for (var i = 0; i < list.length; i++) {
      var entry = list[i];
      var initialOffset = entry.opts.offset !== undefined ? entry.opts.offset : DEFAULT_OFFSET;
      var gap = entry.opts.gap !== undefined ? entry.opts.gap : DEFAULT_GAP;
      var newOffset = _calcOffset(list, i, initialOffset, gap);
      if (newOffset !== entry.offset) {
        entry.offset = newOffset;
        entry.el.style[vProp] = newOffset + 'px';
      }
    }
  }

  /* ── 构建 DOM ── */
  function _buildEl(id, opts, placement) {
    var tone = opts.tone || 'default';
    var showClose = opts.showClose !== false;
    var hasAction = !!opts.action;
    var hasIcon = tone !== 'default';
    var isBottom = placement.startsWith('bottom');
    var isCenter = placement.endsWith('center');

    var el = document.createElement('div');
    el.id = 'dsm-toast-' + id;
    el.className = [
      'dsm-toast',
      'dsm-toast--' + tone,
      isBottom ? 'dsm-toast--from-bottom' : '',
      isCenter ? 'dsm-toast--center' : (placement.endsWith('right') ? 'dsm-toast--right' : 'dsm-toast--left'),
      'dsm-toast--entering'
    ].join(' ').replace(/\s+/g, ' ').trim();

    el.setAttribute('role', 'status');
    el.setAttribute('aria-atomic', 'true');
    el.setAttribute('aria-live', 'polite');

    /* 水平定位（inline style，CSS class 负责 left/right/center transform） */
    var hOffset = opts.offset !== undefined ? opts.offset : DEFAULT_OFFSET;
    var vProp = _vProp(placement);
    /* top/bottom 初始值（后续 _reflow 会更新） */
    el.style.cssText = _hStyle(placement, hOffset);
    el.style[vProp] = hOffset + 'px';

    /* content group */
    var content = document.createElement('div');
    content.className = 'dsm-toast__content';

    if (hasIcon) {
      var icon = document.createElement('span');
      icon.className = 'dsm-toast__icon';
      icon.innerHTML = ICONS[tone] || '';
      content.appendChild(icon);
    }

    var msg = document.createElement('p');
    msg.className = 'dsm-toast__message';
    msg.textContent = opts.message || '';
    content.appendChild(msg);
    el.appendChild(content);

    /* action */
    if (hasAction) {
      var action = document.createElement('button');
      action.type = 'button';
      action.className = 'dsm-toast__action';
      action.textContent = opts.action;
      action.addEventListener('click', function () {
        if (typeof opts.onAction === 'function') opts.onAction();
        dismiss(id);
      });
      el.appendChild(action);
    }

    /* close */
    if (showClose) {
      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'dsm-toast__close';
      closeBtn.setAttribute('aria-label', 'Dismiss notification');
      closeBtn.innerHTML = CLOSE_ICON;
      closeBtn.addEventListener('click', function () { dismiss(id); });
      el.appendChild(closeBtn);
    }

    /* 进入动画结束后移除 --entering 类，避免动画持续占用 will-change */
    el.addEventListener('animationend', function _onEnter(e) {
      if (e.animationName && e.animationName.indexOf('dsm-toast-in') === 0) {
        el.classList.remove('dsm-toast--entering');
        el.removeEventListener('animationend', _onEnter);
      }
    });

    return el;
  }

  /* ── 计时器 ── */
  function _startTimer(entry) {
    if (!entry || entry.remaining <= 0) return;
    entry.startedAt = Date.now();
    entry.timerId = setTimeout(function () { dismiss(entry.id); }, entry.remaining);
  }
  function _pauseTimer(entry) {
    if (!entry || !entry.timerId) return;
    clearTimeout(entry.timerId);
    entry.timerId = null;
    entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
  }
  function _resumeTimer(entry) {
    if (!entry || entry.timerId) return;
    if (entry.remaining > 0) _startTimer(entry);
  }

  /* ── 找 entry by id（跨所有 placement）── */
  function _findEntry(id) {
    for (var p in _instances) {
      var list = _instances[p];
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === id) return { entry: list[i], list: list, placement: p, idx: i };
      }
    }
    return null;
  }

  /* ── 公开 API：show ── */
  function show(opts) {
    if (!opts || !opts.message) return '';

    /* 规范化 placement */
    var placement = opts.placement || DEFAULT_PLACEMENT;
    if (VALID_PLACEMENTS.indexOf(placement) === -1) {
      console.warn('[DsmToast] invalid placement "' + placement + '", fallback to "' + DEFAULT_PLACEMENT + '"');
      placement = DEFAULT_PLACEMENT;
    }
    opts = Object.assign({}, opts, { placement: placement });

    /* grouping：相同 message + tone 则复用，计数器 +1 */
    if (opts.grouping) {
      var list = _getList(placement);
      for (var g = 0; g < list.length; g++) {
        var ex = list[g];
        if (ex.opts.message === opts.message && (ex.opts.tone || 'default') === (opts.tone || 'default')) {
          /* 更新计数 badge */
          ex.repeatNum = (ex.repeatNum || 1) + 1;
          var badge = ex.el.querySelector('.dsm-toast__badge');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'dsm-toast__badge';
            ex.el.insertBefore(badge, ex.el.firstChild);
          }
          badge.textContent = String(ex.repeatNum);
          /* 重置计时器 */
          _pauseTimer(ex);
          _startTimer(ex);
          return ex.id;
        }
      }
    }

    /* appendTo */
    var container = _resolveContainer(opts.appendTo);

    _idCounter += 1;
    var id = String(_idCounter);
    var el = _buildEl(id, opts, placement);

    /* 计算 duration */
    var tone = opts.tone || 'default';
    var duration = opts.duration !== undefined
      ? opts.duration
      : (opts.action ? Math.max(5000, DEFAULT_DURATION[tone] || 3000) : (DEFAULT_DURATION[tone] || 3000));

    var entry = {
      id: id,
      el: el,
      height: 0,
      offset: opts.offset !== undefined ? opts.offset : DEFAULT_OFFSET,
      opts: opts,
      timerId: null,
      remaining: duration,
      startedAt: 0,
      repeatNum: 1,
      ro: null
    };

    /* 追加到 DOM + 实例列表 */
    container.appendChild(el);
    _getList(placement).push(entry);

    /* 首次 layout 后 _reflow（等 DOM paint 再算高度） */
    requestAnimationFrame(function () {
      entry.height = el.getBoundingClientRect().height || 44;
      _reflow(placement);

      /* ResizeObserver：toast 内容变化时重算高度并重排 */
      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(function () {
          var newH = el.getBoundingClientRect().height;
          if (newH && newH !== entry.height) {
            entry.height = newH;
            _reflow(placement);
          }
        });
        ro.observe(el);
        entry.ro = ro;
      }

      /* 计时 */
      if (duration > 0) _startTimer(entry);
    });

    /* hover / focus / touch 暂停 */
    el.addEventListener('mouseenter', function () { _pauseTimer(entry); });
    el.addEventListener('mouseleave', function () { _resumeTimer(entry); });
    el.addEventListener('focusin', function () { _pauseTimer(entry); });
    el.addEventListener('focusout', function () { _resumeTimer(entry); });
    el.addEventListener('touchstart', function () { _pauseTimer(entry); }, { passive: true });
    el.addEventListener('touchend', function () { _resumeTimer(entry); }, { passive: true });

    el.dispatchEvent(new CustomEvent('dsm:toast:show', {
      bubbles: true, detail: { id: id, options: opts }
    }));

    return id;
  }

  /* ── 公开 API：dismiss ── */
  function dismiss(id) {
    var found = _findEntry(id);
    if (!found) return;

    var entry = found.entry;
    var list = found.list;
    var placement = found.placement;

    /* 停计时 */
    if (entry.timerId) { clearTimeout(entry.timerId); entry.timerId = null; }

    /* 停 ResizeObserver */
    if (entry.ro) { entry.ro.disconnect(); entry.ro = null; }

    /* 退出动画 */
    entry.el.classList.add('dsm-toast--exiting');

    setTimeout(function () {
      if (entry.el.parentNode) entry.el.parentNode.removeChild(entry.el);

      /* 从列表移除 */
      var idx = list.indexOf(entry);
      if (idx !== -1) list.splice(idx, 1);

      /* 重排后续 toast */
      _reflow(placement);

      if (typeof opts.onClose === 'function') { try { entry.opts.onClose(); } catch (e) { } }

      entry.el.dispatchEvent(new CustomEvent('dsm:toast:dismiss', {
        bubbles: true, detail: { id: id }
      }));
    }, EXIT_MS);
  }

  /* ── 公开 API：closeAll ── */
  function closeAll(placement) {
    if (placement) {
      var list = (_instances[placement] || []).slice();
      list.forEach(function (e) { dismiss(e.id); });
    } else {
      for (var p in _instances) {
        var all = _instances[p].slice();
        all.forEach(function (e) { dismiss(e.id); });
      }
    }
  }

  return { show: show, dismiss: dismiss, closeAll: closeAll };
})();

window.DsmToast = DsmToast;

window.DsmDialog = DsmDialog;
